import { useRef, useState } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

function PreviewTable({ rows }) {
  return (
    <div className="border rounded-lg max-h-[50vh] overflow-auto">
      <Table>
        <TableHeader><TableRow><TableHead>Riga</TableHead><TableHead>Corsista</TableHead><TableHead>Codice fiscale</TableHead><TableHead>Corso</TableHead><TableHead>Edizione</TableHead><TableHead>CFP</TableHead><TableHead>Esito</TableHead></TableRow></TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.row} className={r.errors.length ? "bg-red-50/60" : ""} data-testid={`import-row-${r.row}`}>
              <TableCell>{r.row}</TableCell><TableCell>{r.student || "—"}</TableCell><TableCell className="text-xs">{r.fiscal_code}</TableCell>
              <TableCell className="text-xs">{r.course_title}</TableCell><TableCell className="text-xs">{r.edition || "—"}</TableCell>
              <TableCell>{r.requires_cfp ? "SI" : "NO"}</TableCell>
              <TableCell className="text-xs">{r.errors.length ? <span className="text-red-700">{r.errors.join(" · ")}</span> : <span className="text-emerald-700 font-semibold">OK</span>}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function ImportEnrollments({ blocked, onImported }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const send = async (dry) => {
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post(`/cliente/enrollments/import?dry_run=${dry}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRes(data);
      if (!dry && data.imported) { toast.success(`${data.imported} iscrizioni importate e confermate`); onImported?.(); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setRes(null); }
    finally { setBusy(false); }
  };
  const pick = (f) => { setFile(f); setRes(null); };
  const reset = () => { setFile(null); setRes(null); if (inputRef.current) inputRef.current.value = ""; };

  if (res?.imported) return (
    <Card className="border-emerald-200 bg-emerald-50" data-testid="import-success"><CardContent className="p-6 space-y-2">
      <div className="flex items-center gap-2 text-emerald-800 font-display text-2xl font-extrabold"><CheckCircle2 className="w-7 h-7" /> {res.imported} iscrizioni importate</div>
      <div className="text-sm text-slate-700">Numeri: {res.enrollment_numbers.join(", ")}</div>
      <Button onClick={reset} variant="outline" data-testid="import-again">Nuova importazione</Button>
    </CardContent></Card>
  );

  return (
    <Card className="border-slate-200" data-testid="import-enrollments"><CardContent className="p-6 space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="text-sm text-slate-600 max-w-xl">
          Scarica il modello Excel: contiene tutti i campi dell'iscrizione (CFP compresi), due righe di esempio, l'elenco dei corsi a catalogo con i codici e le date delle edizioni, e le istruzioni.
        </div>
        <Button asChild variant="outline" data-testid="import-template-download">
          <a href={`${API}/cliente/enrollments/import-template`}><Download className="w-4 h-4 mr-1" /> Scarica modello Excel</a>
        </Button>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <input ref={inputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => pick(e.target.files?.[0] || null)} data-testid="import-file-input" />
        <Button variant="outline" onClick={() => inputRef.current?.click()} data-testid="import-file-pick"><FileSpreadsheet className="w-4 h-4 mr-1" /> Scegli file</Button>
        <span className="text-sm text-slate-600" data-testid="import-file-name">{file?.name || "Nessun file selezionato"}</span>
        <Button onClick={() => send(true)} disabled={!file || busy} className="bg-[#0F2942] hover:bg-[#1B3B5F]" data-testid="import-verify">
          {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Verifica file
        </Button>
      </div>
      {res && (
        <div className="space-y-3" data-testid="import-preview">
          <div className={`rounded-xl border p-3 text-sm font-medium ${res.valid ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-700"}`} data-testid="import-summary">
            {res.valid ? <><CheckCircle2 className="w-4 h-4 inline mr-1" />{res.total} righe valide, pronte per l'importazione.</>
              : <><AlertTriangle className="w-4 h-4 inline mr-1" />{res.errors_count > 0 ? `${res.errors_count} righe su ${res.total} contengono errori: correggi il file e ricaricalo.` : ""}{" "}
                {!res.slots_ok && `Slot insufficienti: servono ${res.slots_needed} slot, disponibili ${res.slots_available}. Contatta l'amministratore per una ricarica.`}</>}
            {res.slot_mode && res.valid && <span> Verranno utilizzati {res.slots_needed} slot su {res.slots_available}.</span>}
          </div>
          <PreviewTable rows={res.rows} />
          <div className="flex justify-end">
            <Button onClick={() => send(false)} disabled={!res.valid || busy || blocked} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="import-confirm">
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Importa {res.total} iscrizioni
            </Button>
          </div>
        </div>
      )}
    </CardContent></Card>
  );
}
